import { Extension } from '@tiptap/core';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import { Decoration, DecorationSet, type EditorView } from '@tiptap/pm/view';

// Read-through highlight (2B, C12). Inert until the player sets it: the
// playing row gets the focused-cell look and a "Now · row N" tag, the phrase
// being spoken is tinted, and the editor root takes `rt-active` so the other
// rows can dim. Decorations only, so the document is never touched.

export interface ReadThroughHighlightState {
  active: boolean;
  /** Start of the playing scriptRow, or null between rows. */
  rowPos: number | null;
  rowNumber: number;
  rowCount: number;
  /** The phrase the voice is reading, in document positions. */
  phrase: { from: number; to: number } | null;
}

const EMPTY: ReadThroughHighlightState = {
  active: false,
  rowPos: null,
  rowNumber: 0,
  rowCount: 0,
  phrase: null,
};

export const readThroughKey = new PluginKey<ReadThroughHighlightState>('hsp-read-through');

export function setReadThroughHighlight(view: EditorView, next: Partial<ReadThroughHighlightState> | null): void {
  if (view.isDestroyed) return;
  view.dispatch(view.state.tr.setMeta(readThroughKey, next ?? EMPTY).setMeta('addToHistory', false));
}

export const ReadThroughHighlight = Extension.create({
  name: 'readThroughHighlight',
  addProseMirrorPlugins() {
    return [
      new Plugin<ReadThroughHighlightState>({
        key: readThroughKey,
        state: {
          init: () => EMPTY,
          apply(tr, value) {
            const meta = tr.getMeta(readThroughKey) as Partial<ReadThroughHighlightState> | undefined;
            let next = meta ? { ...value, ...meta } : value;
            if (tr.docChanged && next.active) {
              next = {
                ...next,
                rowPos: next.rowPos === null ? null : tr.mapping.map(next.rowPos),
                phrase: next.phrase
                  ? { from: tr.mapping.map(next.phrase.from), to: tr.mapping.map(next.phrase.to) }
                  : null,
              };
            }
            return next;
          },
        },
        props: {
          attributes(state): Record<string, string> {
            return readThroughKey.getState(state)?.active ? { class: 'rt-active' } : {};
          },
          decorations(state) {
            const value = readThroughKey.getState(state);
            if (!value?.active || value.rowPos === null) return null;
            const row = state.doc.nodeAt(value.rowPos);
            if (!row || row.type.name !== 'scriptRow') return null;
            const decos = [
              Decoration.node(value.rowPos, value.rowPos + row.nodeSize, {
                class: 'rt-now',
                'data-rt-tag': `Now · row ${value.rowNumber}`,
                'data-rt-tag-full': `Now · row ${value.rowNumber} of ${value.rowCount}`,
              }),
            ];
            const p = value.phrase;
            if (p && p.to > p.from && p.to <= state.doc.content.size) {
              decos.push(Decoration.inline(p.from, p.to, { class: 'rt-word-now' }));
            }
            return DecorationSet.create(state.doc, decos);
          },
        },
      }),
    ];
  },
});
